/** Shadcn-style calendar powered by React DayPicker's keyboard and date semantics. */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ComponentProps, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { DayPicker } from "react-day-picker";
import { arSA, enGB, fr } from "react-day-picker/locale";

import { directionFor } from "@/i18n";
import { cn } from "@/shared/lib/format";
import { buttonVariants } from "@/shared/ui/button";

/** Localized calendar with a single roving tab stop and arrow-key navigation. */
export function Calendar({
  className,
  classNames,
  ...props
}: ComponentProps<typeof DayPicker>): ReactElement {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  return (
    <DayPicker
      locale={language === "ar" ? arSA : language === "fr" ? fr : enGB}
      dir={directionFor(language)}
      showOutsideDays
      className={cn("w-full p-3", className)}
      classNames={{
        months: "relative",
        month: "space-y-3",
        month_caption: "flex h-9 items-center justify-center px-10",
        caption_label: "text-sm font-medium",
        nav: "absolute inset-x-0 top-0 flex justify-between",
        button_previous: cn(buttonVariants({ variant: "ghost", size: "icon" }), "size-9"),
        button_next: cn(buttonVariants({ variant: "ghost", size: "icon" }), "size-9"),
        month_grid: "w-full table-fixed border-collapse",
        weekday: "h-8 text-xs font-normal text-muted-foreground",
        day: "p-0.5 text-center text-sm",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "size-9 w-full min-h-9 p-0 font-normal tabular-nums",
        ),
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary/90",
        today: "[&:not([aria-selected=true])>button]:bg-accent [&>button]:font-semibold",
        outside: "text-muted-foreground",
        disabled: "opacity-40",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="size-4 rtl:rotate-180" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-4 rtl:rotate-180" aria-hidden="true" />
          ),
      }}
      {...props}
    />
  );
}
